/**
 * GRAVITY BEAM: hold LMB/E and everything loose nearby gets sucked toward
 * you; pieces that reach the beam join an orbiting cluster (up to MAX_HELD);
 * release launches the WHOLE VOLLEY along ballistic lobs at your cursor with
 * a slight spread. Held bodies stay dynamic and velocity-driven (PD), so
 * they collide gracefully instead of clipping.
 *
 * Throw model: fixed flight-time solve — every throw is a readable parabola.
 * A dotted trajectory preview shows the cluster's flight while holding.
 */
import * as THREE from "three";
import type { TrashKind, TrashPiece } from "./Trash.js";
import { PORTAL_INWARD_WINDOW_SECONDS } from "./PortalPair.js";
import type { InputFrame } from "../core/Input.js";

/**
 * Default beam bulk capacity (LOCKED-DESIGN 4.1). Simple mode now carries the
 * SAME 6 via the seed-carried `rules.beamCapacity` (LOCKED-DESIGN 18.15 /
 * PLAYTEST F1: "6 blocks or 3 cylinders" is exactly 6 bulk; 18.11 v2's 3-bulk
 * "smaller scoops" is superseded). The Engine still (re)applies the run's
 * capacity in resetWorld from the CURRENT seed, so a future narrowed ruleset can
 * never leak across a mode toggle. MAX_HELD stays 6 (the line-buffer / orbit-slot
 * ceiling) and still bounds the orbit: the new worst case is 6 crates = 6 bulk =
 * 6 pieces = EXACTLY MAX_HELD, so the slots are full but never oversubscribed
 * (any can/green in the scoop costs >=2 bulk per piece and lands under 6 pieces).
 */
const DEFAULT_MAX_BULK = 6;
const MAX_HELD = 6;
const VACUUM_RANGE = 9; // suction reach from the hold point
const VACUUM_BLEND = 0.16; // per-step velocity blend toward the beam
const VACUUM_SPEED = 15; // suction cruise speed
const CAPTURE_DIST = 2.6; // joins the cluster inside this
const HOLD_DIST = 2.2;
const HOLD_HEIGHT = 1.6;
const HOLD_STIFFNESS = 13; // velocity = error * stiffness
const HOLD_MAX_SPEED = 34;
const BREAK_DIST = 9;
const ORBIT_RADIUS = 0.8;
const ORBIT_SPEED = 2.4; // rad/s

const GRAVITY = 9.81; // world gravity for trash (NOT the player's gamey gravity)
const ARC_HORIZ_SPEED = 14; // m/s horizontal pace of a throw
const ARC_T_MIN = 0.45;
const ARC_T_MAX = 1.35;
/**
 * Hard ceiling on the speed a release may leave at (PLAYTEST F18, twelfth
 * dogfood: "i noticed another item fly through the glass", still, after F16).
 *
 * The fixed-flight-time solve below is unbounded by construction: T is floored
 * at ARC_T_MIN while `d` and `h` come from wherever the aim point is, so an aim
 * point far outside the room turns straight into launch speed. `Engine.updateAim`
 * used to hand it points ~31 m under the floor (no floor/wall backstop on the aim
 * raycast, PLAYTEST F18), and the game's own release path then produced 71.5 m/s.
 *
 * Trash bodies run `setSoftCcdPrediction(1)` (PLAYTEST F16, Trash.ts) with
 * Rapier's `maxCcdSubsteps = 1`: soft CCD covers exactly 1.0 m of travel per
 * fixed step, i.e. 60 m/s, and above that the reinforced-glass seal is on hard
 * CCD alone — the mode F16 itself measured leaking at 2/54,430. The QA sweep in
 * `arena.qa.pw.ts` validates the seal head-on to 54 m/s.
 *
 * 40 m/s = 0.667 m per fixed step: clear of HOLD_MAX_SPEED (34) so the beam can
 * still fling as hard as it can drag, clear of the ~38 m/s the QA glass spec's
 * long aims solve for so that spec keeps exercising the defect it was written
 * for, and a third under the soft-CCD horizon with the whole validated sweep to
 * spare. It is a CEILING, never a target: see `fitFlightTime` — the cap is
 * honoured by flying a slower, loftier arc to the SAME point, so no throw the
 * player could aim inside this building is shortened by it.
 */
const THROW_MAX_SPEED = 40;
const VOLLEY_SPREAD = 0.7; // per-piece random target offset

const ASSIST_MISS_FACTOR = 2.2;
const ASSIST_MAX_BLEND = 0.75;
/**
 * Point-blank assist floor (PLAYTEST F5, LOCKED-DESIGN 18.17): inside this
 * distance the falloff term is clamped so the pull can never fade below half
 * of `maxBlend` — the assist used to switch OFF entirely inside 2.0 m, which is
 * exactly the band where a player aiming carefully at a close portal needs it.
 * The CAP is unchanged; only the floor is new.
 */
const ASSIST_NEAR_DIST = 2.0;
const ASSIST_NEAR_FLOOR = 0.5;
/**
 * How far IN FRONT of an aimed portal's plane a release is solved from
 * (PLAYTEST F5). A held volley floats HOLD_DIST (2.2 m) ahead of the player, so
 * a throw from closer than that at a wall/panel portal starts BEHIND the plane
 * and the ballistic solve aims it further away. Clamping the solve origin in
 * front of the plane turns that into a throw that actually crosses the disc.
 */
const RELEASE_FRONT_MARGIN = 0.35;
/**
 * Deepest a release may sit BEHIND an aimed disc and still be re-solved from in
 * front of it. DERIVED from the portal's own acceptance window, not tuned:
 * `PortalPair` takes an `"inward"` body down to `TRASH_RADIUS + INWARD_BEHIND_
 * MARGIN` = 0.45 + 1.2 = 1.65 m behind the plane, so a piece deeper than that
 * could not be swallowed however the throw is solved — re-solving it would only
 * distort an ordinary throw that happens to start behind a panel anchor. The
 * intended F5 case (a volley floating HOLD_DIST ahead of a player who cannot
 * stand closer than their capsule radius) never exceeds 1.2 m, so the widest
 * legal nudge is 1.55 m.
 */
const RELEASE_MAX_BEHIND = 1.65;

const PREVIEW_POINTS = 24;

export interface AimAssistTarget {
  center: THREE.Vector3;
  radius: number;
  /** override blend cap (gamepad aim gets console-grade near-snap) */
  maxBlend?: number;
  /**
   * Outward normal of the aimed portal's disc, when the target IS a portal.
   * Present only for live portals (see Engine.assistTarget), and used solely to
   * clamp the release origin in front of the plane. Absent → no clamp.
   */
  normal?: THREE.Vector3;
}

export interface BeamStepResult {
  threw: TrashPiece[];
  captured: TrashPiece[];
}

export class GrabBeam {
  private readonly held: TrashPiece[] = [];
  private orbitPhase = 0;
  /** Max bulk this beam holds (LOCKED-DESIGN 4.1; Simple v2 narrows to 3). */
  private maxBulk = DEFAULT_MAX_BULK;
  /**
   * Simple v2 (LOCKED-DESIGN 4.1 capacity-blocked): when on, a loose piece too
   * big for the remaining capacity is left on the floor instead of vacuumed to
   * the beam and glued there forever (it can never capture). Official 6-bulk
   * play leaves this OFF, so its suction physics stay byte-identical.
   */
  private blockOversize = false;
  /** True on the fixed step the beam first reached capacity while holding. */
  private toppedOutThisStep = false;
  /** True on a step where a too-big piece was left loose (drives the kid cue). */
  private capacityBlockedThisStep = false;

  /** beam visuals: one segment per held piece */
  readonly lines: THREE.LineSegments;
  private readonly linesGeo: THREE.BufferGeometry;

  /** dotted trajectory preview (visible while holding) */
  readonly preview: THREE.Line;
  private readonly previewGeo: THREE.BufferGeometry;

  private readonly v1 = new THREE.Vector3();
  private readonly v2 = new THREE.Vector3();
  private readonly v3 = new THREE.Vector3();
  private readonly holdCenter = new THREE.Vector3();

  constructor() {
    this.linesGeo = new THREE.BufferGeometry();
    this.linesGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(MAX_HELD * 2 * 3), 3),
    );
    this.lines = new THREE.LineSegments(
      this.linesGeo,
      new THREE.LineBasicMaterial({ color: 0x3bc9a8, transparent: true, opacity: 0.8 }),
    );
    this.lines.visible = false;
    this.lines.frustumCulled = false;

    this.previewGeo = new THREE.BufferGeometry();
    this.previewGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(PREVIEW_POINTS * 3), 3),
    );
    this.preview = new THREE.Line(
      this.previewGeo,
      new THREE.LineDashedMaterial({
        color: 0xdfe7f2,
        transparent: true,
        opacity: 0.55,
        dashSize: 0.35,
        gapSize: 0.25,
      }),
    );
    this.preview.visible = false;
    this.preview.frustumCulled = false;
  }

  isHolding(): boolean {
    return this.held.length > 0;
  }

  /**
   * Set the beam's bulk capacity for a run (LOCKED-DESIGN 18.11 v2, 18.15).
   * Engine calls this in resetWorld from the current seed's `rules.beamCapacity`
   * (6 everywhere since 18.15 — official AND Simple), so mode toggles between
   * runs never leak a narrowed capacity into adult play.
   */
  setCapacity(bulk: number, blockOversize = false): void {
    this.maxBulk = Math.max(1, Math.round(bulk));
    this.blockOversize = blockOversize;
  }

  /** Current bulk capacity (reticle + fullness cues read this, never a literal 6). */
  get capacity(): number {
    return this.maxBulk;
  }

  /** True at capacity while holding — the in-world fullness cue (no suction / outline). */
  isFull(): boolean {
    return this.heldBulk() >= this.maxBulk;
  }

  /** Consume the "topped out this step" edge (the one-shot FULL UP toast trigger). */
  consumeToppedOut(): boolean {
    const v = this.toppedOutThisStep;
    this.toppedOutThisStep = false;
    return v;
  }

  /**
   * Consume the "capacity-blocked this step" edge (Simple v2): a loose piece was
   * too big for the remaining capacity and left on the floor. Drives the same
   * kid "FULL UP! THROW IT!" nudge so a 7-year-old learns to throw first instead
   * of staring at trash the beam won't pull in. Always false in official play.
   */
  consumeCapacityBlocked(): boolean {
    const v = this.capacityBlockedThisStep;
    this.capacityBlockedThisStep = false;
    return v;
  }

  heldCount(): number {
    return this.held.length;
  }

  heldBulk(): number { return this.held.reduce((sum, piece) => sum + piece.bulk, 0); }
  /**
   * Held-load composition for the reticle dock (LOCKED-DESIGN 12.1). Read-only
   * presentation surface: kind → count in stable spawn order. Never mutates the
   * beam and is never fed back into the simulation.
   */
  heldComposition(): Array<{ kind: TrashKind; count: number }> {
    const order: TrashKind[] = [];
    const counts = new Map<TrashKind, number>();
    for (const piece of this.held) {
      if (!counts.has(piece.kind)) order.push(piece.kind);
      counts.set(piece.kind, (counts.get(piece.kind) ?? 0) + 1);
    }
    return order.map((kind) => ({ kind, count: counts.get(kind)! }));
  }
  redFuseSeconds(): number | null {
    const ticks = this.held.flatMap((piece) => piece.fuseTicks === null ? [] : [piece.fuseTicks]);
    return ticks.length ? Math.max(0, Math.min(...ticks) / 60) : null;
  }

  /** One fixed step. playerPos = capsule center; aimPoint = cursor world hit. */
  step(
    dt: number,
    input: InputFrame,
    playerPos: THREE.Vector3,
    aimPoint: THREE.Vector3,
    pieces: TrashPiece[],
    assist?: AimAssistTarget,
    playerInChamber = false,
  ): BeamStepResult {
    const result: BeamStepResult = { threw: [], captured: [] };
    this.toppedOutThisStep = false;
    this.capacityBlockedThisStep = false;
    const wasFull = this.isFull();

    // hold point: toward aim, horizontally, at chest height
    this.holdCenter.copy(aimPoint).sub(playerPos);
    this.holdCenter.y = 0;
    if (this.holdCenter.lengthSq() < 0.01) this.holdCenter.set(0, 0, -1);
    this.holdCenter.normalize().multiplyScalar(HOLD_DIST);
    this.holdCenter.add(playerPos);
    this.holdCenter.y = playerPos.y + HOLD_HEIGHT - 0.5;

    // ---- release: the whole volley flies ----
    if (this.held.length > 0 && input.grabReleased) {
      for (let index = 0; index < this.held.length; index++) {
        const piece = this.held[index]!;
        piece.held = false;
        if (!piece.live) continue;
        const t = piece.body.translation();
        this.v2.set(t.x, t.y, t.z);
        // F5: solve from IN FRONT of the aimed portal's plane. Per piece —
        // every held body has its own translation in the orbit cluster. Only a
        // release that was actually re-solved (aimed at the disc, inside the
        // window) opens the portal's widened `"inward"` capture for this piece;
        // every other throw stays on strict capture, so debris that merely ends
        // up behind a panel is never quietly delivered (LOCKED-DESIGN 18.17).
        const resolved = clampReleaseOrigin(this.v2, aimPoint, assist);
        piece.portalInward = resolved ? PORTAL_INWARD_WINDOW_SECONDS : 0;
        // per-piece spread so the volley fans
        this.v3.copy(aimPoint);
        this.applyAimAssist(this.v3, this.v2, assist);
        const fan = index - (this.held.length - 1) / 2;
        this.v3.x += fan * VOLLEY_SPREAD * .35;
        ballisticVelocity(this.v1, this.v2, this.v3, GRAVITY);
        piece.body.setLinvel({ x: this.v1.x, y: this.v1.y, z: this.v1.z }, true);
        piece.body.wakeUp();
        piece.portalCooldown = 0;
        piece.portalArmed = true;
        result.threw.push(piece);
      }
      this.held.length = 0;
      return result;
    }

    if (!input.grabHeld) {
      if (this.held.length > 0) this.dropAll(); // safety: missed release edge
      return result;
    }

    // ---- vacuum: pull everything loose in range toward the beam ----
    const candidates = pieces.filter((p) => p.live && !p.held).sort((a, b) => {
      const at = a.body.translation(); const bt = b.body.translation();
      const ad = (at.x - this.holdCenter.x) ** 2 + (at.y - this.holdCenter.y) ** 2 + (at.z - this.holdCenter.z) ** 2;
      const bd = (bt.x - this.holdCenter.x) ** 2 + (bt.y - this.holdCenter.y) ** 2 + (bt.z - this.holdCenter.z) ** 2;
      return ad - bd || a.id - b.id;
    });
    for (const p of candidates) {
      if (!p.live || p.held) continue;
      // The glass stops the beam (PLAYTEST F21, owner call). VACUUM_RANGE (9 m)
      // and CAPTURE_DIST (2.6 m) are pure spherical range tests around a hold
      // point that sits HOLD_DIST (2.2 m) ahead of the player, so a player at
      // the bulkhead had a hold point 1.24 m PAST the pane's north face and
      // could haul chamber contents at the glass from as far back as z −9 —
      // measured at 32.71 m/s — including dragging rejects off the jam pile they
      // are supposed to ride in and hand-feed. Nothing physically crossed (the
      // pane holds), but the reach was real and it undercut the whole recovery
      // loop. `p.chamber` is a positional fact since F19, recomputed every step
      // from `inChamberVolume`, so same-side is the whole test: it costs one
      // boolean compare, needs no raycast, and is automatically correct for the
      // open north shoulder (|x| > 12), which is NOT behind glass and stays
      // reachable. Retention is deliberately untouched — this gates ACQUISITION
      // only, so a volley already in hand rides through a portal with its owner.
      if (p.chamber !== playerInChamber) continue;
      const t = p.body.translation();
      this.v1.set(this.holdCenter.x - t.x, this.holdCenter.y - t.y, this.holdCenter.z - t.z);
      const d = this.v1.length();
      if (d > VACUUM_RANGE) continue;

      const heldBulk = this.held.reduce((sum, held) => sum + held.bulk, 0);
      if (heldBulk >= this.maxBulk) continue; // full: no more capture (in-world fullness cue)
      const fits = heldBulk + p.bulk <= this.maxBulk;
      // Simple v2 (LOCKED-DESIGN 4.1): a piece too big for the remaining capacity
      // is left loose instead of vacuumed to the beam and glued there forever —
      // e.g. a fourth can (2 bulk) with 5 of 6 bulk already held. Flag the
      // block so the Engine can nudge the kid to throw first. Smaller pieces
      // that DO fit are still captured normally by the check below.
      if (this.blockOversize && !fits) {
        this.capacityBlockedThisStep = true;
        continue;
      }
      if (d < CAPTURE_DIST && fits) {
        this.held.push(p);
        p.held = true;
        result.captured.push(p);
        continue;
      }
      // blend velocity toward the beam — stronger when closer
      const pull = VACUUM_SPEED * (1 - (d / VACUUM_RANGE) * 0.55);
      this.v1.normalize().multiplyScalar(pull);
      const lv = p.body.linvel();
      p.body.setLinvel(
        {
          x: lv.x * (1 - VACUUM_BLEND) + this.v1.x * VACUUM_BLEND,
          y: lv.y * (1 - VACUUM_BLEND) + this.v1.y * VACUUM_BLEND + 0.35, // fight gravity a bit
          z: lv.z * (1 - VACUUM_BLEND) + this.v1.z * VACUUM_BLEND,
        },
        true,
      );
    }

    // Topped-out edge (LOCKED-DESIGN 18.11 v2): the beam reached capacity THIS
    // step while holding — drives the one-shot kid "FULL UP! THROW IT!" toast.
    if (!wasFull && this.isFull()) this.toppedOutThisStep = true;

    // ---- drive the cluster: orbit slots around the hold point ----
    this.orbitPhase += dt * ORBIT_SPEED;
    for (let i = this.held.length - 1; i >= 0; i--) {
      const piece = this.held[i]!;
      if (!piece.live) {
        this.held.splice(i, 1);
        continue;
      }
      const t = piece.body.translation();
      const n = this.held.length;
      const ang = this.orbitPhase + (i * Math.PI * 2) / Math.max(1, n);
      // ring widens as the cluster grows so a 12-piece volley doesn't jostle
      const r = n > 1 ? ORBIT_RADIUS * (1 + n * 0.06) + (i % 2) * 0.3 : 0;
      this.v1.set(
        this.holdCenter.x + Math.cos(ang) * r,
        this.holdCenter.y + ((i % 3) - 1) * 0.3,
        this.holdCenter.z + Math.sin(ang) * r,
      );
      this.v2.set(this.v1.x - t.x, this.v1.y - t.y, this.v1.z - t.z);
      if (this.v2.lengthSq() > BREAK_DIST * BREAK_DIST) {
        piece.held = false;
        this.held.splice(i, 1);
        continue;
      }
      this.v2.multiplyScalar(HOLD_STIFFNESS);
      if (this.v2.length() > HOLD_MAX_SPEED) this.v2.setLength(HOLD_MAX_SPEED);
      piece.body.setLinvel({ x: this.v2.x, y: this.v2.y, z: this.v2.z }, true);
      const av = piece.body.angvel();
      piece.body.setAngvel({ x: av.x * 0.9, y: av.y * 0.9, z: av.z * 0.9 }, true);
    }

    return result;
  }

  /** Assisted target + flight-time ballistic solve. Shared by throw and preview. */
  private solvePreview(outVel: THREE.Vector3, origin: THREE.Vector3, aimPoint: THREE.Vector3, assist?: AimAssistTarget): void {
    this.v3.copy(aimPoint);
    this.applyAimAssist(this.v3, origin, assist);
    ballisticVelocity(outVel, origin, this.v3, GRAVITY);
  }

  private applyAimAssist(target: THREE.Vector3, origin: THREE.Vector3, assist?: AimAssistTarget): void {
    if (!assist) return;
    const miss = target.distanceTo(assist.center);
    const missR = assist.radius * ASSIST_MISS_FACTOR;
    if (miss >= missR) return;
    const maxBlend = assist.maxBlend ?? ASSIST_MAX_BLEND;
    // sqrt falloff: strong pull through most of the capture zone. F5: inside
    // ASSIST_NEAR_DIST the falloff term is floored instead of the whole assist
    // being switched off, so a point-blank throw still gets at least half the
    // pull. The cap (maxBlend) is untouched, so no throw is pulled harder than
    // before at any distance.
    const near = assist.center.distanceTo(origin) < ASSIST_NEAR_DIST;
    const falloff = Math.sqrt(1 - miss / missR);
    const w = (near ? Math.max(falloff, ASSIST_NEAR_FLOOR) : falloff) * maxBlend;
    target.lerp(assist.center, w);
  }

  private dropAll(): void {
    for (const p of this.held) p.held = false;
    this.held.length = 0;
  }

  forceDrop(): void {
    this.dropAll();
  }

  purgeHeld(): TrashPiece[] {
    const pieces = [...this.held];
    this.held.length = 0;
    for (const piece of pieces) piece.held = false;
    return pieces;
  }

  /** Per-render-frame visuals: beam segments + cluster trajectory preview. */
  updateVisual(
    playerRenderPos: THREE.Vector3,
    aimPoint: THREE.Vector3,
    assist?: AimAssistTarget,
  ): void {
    if (this.held.length === 0) {
      this.lines.visible = false;
      this.preview.visible = false;
      return;
    }
    this.lines.visible = true;
    const attr = this.linesGeo.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i < MAX_HELD; i++) {
      const piece = this.held[i];
      if (piece && piece.live) {
        attr.setXYZ(i * 2, playerRenderPos.x, playerRenderPos.y + 0.6, playerRenderPos.z);
        const m = piece.mesh.position;
        attr.setXYZ(i * 2 + 1, m.x, m.y, m.z);
      } else {
        // collapse unused segments
        attr.setXYZ(i * 2, 0, -1000, 0);
        attr.setXYZ(i * 2 + 1, 0, -1000, 0);
      }
    }
    attr.needsUpdate = true;

    // preview from the cluster's hold center
    this.preview.visible = true;
    this.solvePreview(this.v1, this.holdCenter, aimPoint, assist);
    const T = lastFlightTime;
    const pattr = this.previewGeo.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i < PREVIEW_POINTS; i++) {
      const t = (T * i) / (PREVIEW_POINTS - 1);
      pattr.setXYZ(
        i,
        this.holdCenter.x + this.v1.x * t,
        this.holdCenter.y + this.v1.y * t - 0.5 * GRAVITY * t * t,
        this.holdCenter.z + this.v1.z * t,
      );
    }
    pattr.needsUpdate = true;
    this.preview.computeLineDistances();
  }
}

/**
 * Push a release origin in front of the aimed portal's plane (PLAYTEST F5,
 * LOCKED-DESIGN 18.17). A held volley floats HOLD_DIST (2.2 m) ahead of the
 * player, so a throw made closer than that at a wall/panel portal starts BEHIND
 * the disc — and a ballistic solve from behind aims the piece back OUT past the
 * player, which is exactly the "I threw it and the game ignored me" bug.
 * Solving from `center + normal * RELEASE_FRONT_MARGIN` instead points the
 * throw through the disc, where PortalPair's `"inward"` capture takes it.
 *
 * SCOPED, because the re-solve moves a throw by up to 2 m and must never touch
 * an ordinary one (a throw made from BEHIND a panel anchor at a pile on the
 * floor used to be teleported to the plane and fired meters past its target):
 *
 *  1. the throw must be AIMED at this disc — the same capture test
 *     `applyAimAssist` uses, so the clamp engages exactly when the magnetism
 *     does and never on a throw pointed somewhere else entirely; and
 *  2. the release must be inside RELEASE_MAX_BEHIND of the plane — deeper than
 *     the portal's own inward window, no solve can deliver the piece anyway.
 *
 * Returns true when the origin was actually re-solved, which is precisely the
 * point-blank release that earns the widened `"inward"` capture window
 * (`TrashPiece.portalInward`); every other release stays on strict capture.
 *
 * Exported so the portal capture suite can exercise the REAL release geometry
 * instead of a copy of it. Mutates `origin` in place. No-op when the assist
 * target carries no normal (not a portal, or a portal that is not live), and
 * no-op for any release already clear of the plane — including floor portals
 * in practice, where a held piece rides ~1.5 m ABOVE the disc.
 */
export function clampReleaseOrigin(
  origin: THREE.Vector3,
  aimPoint: THREE.Vector3,
  assist?: AimAssistTarget,
): boolean {
  const n = assist?.normal;
  if (!n) return false;
  const c = assist.center;
  // (1) aimed at THIS disc: identical gate to the aim assist's capture zone.
  if (aimPoint.distanceTo(c) >= assist.radius * ASSIST_MISS_FACTOR) return false;
  const s = (origin.x - c.x) * n.x + (origin.y - c.y) * n.y + (origin.z - c.z) * n.z;
  if (s >= RELEASE_FRONT_MARGIN) return false;
  // (2) bounded: a release deeper than the portal would ever accept is left
  // exactly where the physics put it.
  if (s < -RELEASE_MAX_BEHIND) return false;
  origin.addScaledVector(n, RELEASE_FRONT_MARGIN - s);
  return true;
}

/** flight time of the most recent ballisticVelocity solve (module-local scratch) */
let lastFlightTime = 1;

/**
 * Smallest flight time >= `T` whose solve leaves at most THROW_MAX_SPEED
 * (PLAYTEST F18). Exact, not a search.
 *
 * Substituting the solve into |v|^2 and writing u = T^2 gives
 *   S(u) = (d^2 + h^2)/u + g*h + (g^2/4)*u
 * which is convex in u, so `S(u) <= V^2` holds on the closed interval between
 * the roots of `(g^2/4)u^2 + (g*h - V^2)u + (d^2 + h^2) = 0`. We are only ever
 * called when S(T^2) > V^2, and the too-fast case is always the SHORT-flight
 * side of the minimum (T caps at ARC_T_MAX = 1.35 s, i.e. u <= 1.82, while the
 * speed minimum sits at u* = 2*sqrt(d^2+h^2)/g, which is past 1.82 for every
 * throw fast enough to trip the cap), so the SMALLER root is the earliest
 * arrival that fits.
 *
 * Capping this way instead of scaling the velocity down keeps the throw landing
 * exactly where it was aimed — it just gets there on a slower, loftier arc, and
 * the dotted preview follows because it shares this solve. The minimum speed any
 * arc to a target can have is sqrt(g*(r + h)) with r = sqrt(d^2 + h^2); across
 * the whole 56 x 40 x 19 room that peaks at 29.8 m/s, so for any point a player
 * can aim at inside this building the cap is provably NON-BINDING on where the
 * throw lands. The discriminant guard only fires for targets outside the room
 * (the analytic ray backstop in `Engine.aimFallbackDistance` keeps aim points in
 * it); there we fly the minimum-energy arc, the slowest solve that still points
 * at the target.
 */
function fitFlightTime(T: number, d: number, h: number, g: number): number {
  const cap = THROW_MAX_SPEED * THROW_MAX_SPEED;
  const reach = d * d + h * h;
  if (reach / (T * T) + g * h + 0.25 * g * g * T * T <= cap) return T;
  const a = 0.25 * g * g;
  const b = g * h - cap;
  const disc = b * b - 4 * a * reach;
  if (disc <= 0) return Math.sqrt((2 * Math.sqrt(reach)) / g);
  return Math.max(T, Math.sqrt(Math.max((-b - Math.sqrt(disc)) / (2 * a), 1e-6)));
}

/**
 * Launch velocity that lobs a projectile from `from` to `to` in a fixed
 * flight time derived from horizontal distance. Always a readable parabola.
 */
export function ballisticVelocity(
  out: THREE.Vector3,
  from: { x: number; y: number; z: number },
  to: THREE.Vector3,
  g: number,
): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const d = Math.hypot(dx, dz);
  const h = to.y - from.y;
  // PLAYTEST F18: the fixed flight time is a FLOOR on the arc, not a promise —
  // it stretches rather than let a solve exceed THROW_MAX_SPEED.
  const T = fitFlightTime(Math.min(ARC_T_MAX, Math.max(ARC_T_MIN, d / ARC_HORIZ_SPEED)), d, h, g);
  out.set(dx / T, (h + 0.5 * g * T * T) / T, dz / T);
  // PLAYTEST F18, unconditional backstop: `fitFlightTime` already lands every
  // in-room target under the cap, so this is inert for any throw the game can
  // produce. It exists so the ceiling is a PROPERTY of ballisticVelocity rather
  // than a consequence of the aim point being sane — the unreachable-target
  // branch above returns a minimum-energy arc that can still be faster than the
  // cap (a 700 m target needs 83 m/s however it is thrown), and no future caller
  // should be able to reintroduce a tunnelling-speed launch by handing this a
  // point outside the building.
  if (out.lengthSq() > THROW_MAX_SPEED * THROW_MAX_SPEED) out.setLength(THROW_MAX_SPEED);
  lastFlightTime = T;
  return T;
}
